import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { isCorrectionEligibilityConflict } from "features/cdm/utils/correctionValidation";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

export const useValidatePP = ({ patientId }) => {
  const queryClient = useQueryClient();

  const handleNetworkError = useHandleNetworkError("Error while validating PP");

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async (data) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/cdm/patients/${patientId}/pp/validate`,
        {
          originalReimbursementMessageControlId:
            data?.reimbursementMessageControlId || null,
          correctionReason: data?.reimbursementMessageControlId
            ? data.correctionReason || null
            : null,
          patientDemographics: {
            ethnicityCode: data.ethnicityCode,
          },
          registrationReasons: data.registrationReasons,
          yearOfRegistration: Number(data.yearOfRegistration),
          additionalRiskFactors: data.additionalRiskFactors ?? [],
          yearOfAdditionalRiskFactor: data.yearOfAdditionalRiskFactor ?? {},
          medicationReview: data.medicationReview === "yes" ? true : false,
          riskFactors: {
            smokingStatus: data.smokingStatus,
            vapingStatus: data.vapingStatus,
            weightKg: Number(data.weight),
            heightCm: Number(data.height),
            bmi: Number(data.bmi),
            waistCircumferenceCm: Number(data.waistCircumference),
            qrisk3Score: Number(data.qRisk3Score),
            alcoholAuditCScore: Number(data.alcoholRiskScore),
            alcoholFullAuditScore: data.alcoholFullAuditScore ? Number(data.alcoholFullAuditScore) : null,
            physicalActivityQ1: data.physicalActivityQ1,
            physicalActivityQ2: data.physicalActivityQ2,
            smokingInterventions: (data.smokingInterventions || []).filter(
              Boolean
            ),
            alcoholDependenceInterventions: (
              data.alcoholDependenceInterventions || []
            ).filter(Boolean),
            alcoholHigherRiskInterventions: (
              data.alcoholHigherRiskInterventions || []
            ).filter(Boolean),
            alcoholIncreasingRiskInterventions: (
              data.alcoholIncreasingRiskInterventions || []
            ).filter(Boolean),
            weightHighRiskInterventions: (
              data.weightHighRiskInterventions || []
            ).filter(Boolean),
            weightIncreasedRiskInterventions: (
              data.weightIncreasedRiskInterventions || []
            ).filter(Boolean),
            weightNormalInterventions: (
              data.weightNormalInterventions || []
            ).filter(Boolean),
            physicalActivityInterventions: (
              data.physicalActivityInterventions || []
            ).filter(Boolean),
            influenzaVaccine: data.influenzaVaccine || null,
            influenzaVaccineDate: data.influenzaVaccineDate
              ? data.influenzaVaccineDate.split("-").reverse().join("-")
              : null,
            pneumococcalVaccine: data.pneumococcalVaccine || null,
            pneumococcalVaccineDate: data.pneumococcalVaccineDate
              ? data.pneumococcalVaccineDate.split("-").reverse().join("-")
              : null,
            covidVaccine: data.covidVaccine || null,
            covidVaccineStatusUpToDate:
              data.covidVaccineStatusUpToDate === "YES",
          },
          physicalExam: {
            pulseRate: Number(data.pulse),
            pulseRhythm: data.pulseRhythm,
            systolicBloodPressure: Number(data.bp1),
            diastolicBloodPressure: Number(data.bp2),
          },
          diagnosticInvestigations: {
            ecgSinceLastReview:
              data.ecgSinceLastReview === "YES" ? true : false,
            ecgResult: data.ecgResult || null,
            echoSinceLastReview:
              data.echoSinceLastReview === "YES" ? true : false,
            echoResult: data.echoResult || null,
          },
          laboratoryInvestigations: {
            haemoglobin: Number(data.haemoglobin),
            totalCholesterol: Number(data.totalCholesterol),
            hdlCholesterol: Number(data.hdlCholesterol),
            nonHdlCholesterol: Number(data.nonHdlCholesterol),
            ldlCholesterol: data.ldlCholesterol,
            triglycerides: Number(data.triglycerides),
            hba1c: Number(data.hba1c),
            serumCreatinine: Number(data.serumCreatinine),
            egfr: data.egfr,
            // FastingGlucose + ACR — pending BE (see KIN ticket for PP labs); ignored until DTO adds them.
            fastingGlucose: data.fastingGlucose || null,
            acr: data.acr === "NA" ? "NA" : Number(data.acr) || null,
            // NT-proBNP — hidden until lab capacity is increased (KIN-1050); ignored until DTO adds it.
            ntProBNP: data.ntProBNP === "NA" ? "NA" : Number(data.ntProBNP) || null,
            // TFT/LFT registration-only — null (not collected) at annual review.
            tftConducted:
              data.tftConducted === "YES"
                ? true
                : data.tftConducted === "NO"
                  ? false
                  : null,
            lftConducted:
              data.lftConducted === "YES"
                ? true
                : data.lftConducted === "NO"
                  ? false
                  : null,
          },
          patientEducation: {
            educationProvided: data.educationProvided || null,
            referredToEducation: data.referredToEducation || null,
            referredToDiabetesPrevention: data.referredToDiabetesPrevention || null,
            referralDetails: data.referralDetails || null,
            agreedWrittenCarePlan: data.agreedWrittenCarePlan || null,
          },
          carePlan: data.carePlan,
        }
      ).catch((error) => {
        // Editing a saved review: BE validate 409s on eligibility — treat as
        // passed and let submit handle the correction.
        if (isCorrectionEligibilityConflict(error, data)) {
          return null;
        }

        throw error;
      });

      if (!response) {
        return { isValid: true };
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-cdm"] });
      queryClient.invalidateQueries({ queryKey: ["patientCDMStatus"] });
    },
    onError: handleNetworkError,
  });
};
